import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  toPackOpenerOptions,
  type AnimationConfig,
  type CardBadge,
  type CardFact,
} from '@game-platform/pack-animation';
import type { PackOpenerHandle } from 'pack-opener-js/react';
import { loadOpener, type OpenerComponent } from './loadOpener';
import { resolveCssColor } from './resolveCssColor';
import type { WinnerCardRest } from './useWinnerCardRest';

export interface PackOpenerStageHandle {
  /** Open without a gesture — the `RIP!`-button path. */
  play(): void;
  /** Put the pack back together and rearm the gesture. */
  reset(): void;
  /** Spin the carousel's ring on; nothing for the other mechanics. */
  shuffle(): void;
  /** Whether the scene reads the finger; off while the host is busy on its behalf. */
  setEnabled(enabled: boolean): void;
}

export interface PackOpenerStageProps {
  animation: AnimationConfig;
  packUrl: string;
  cardUrl?: string;
  /** The stand under the revealed card — the pack's own, or the product default. */
  pedestalUrl?: string;
  /** Any CSS colour expression; `var(--ui-badge-…)` is resolved before Pixi sees it. */
  glow: string;
  /** The carousel's story, once the pull is known; the other mechanics read neither. */
  facts?: CardFact[];
  /** The tier the carousel shouts and slice/burst write behind the card — its colour any CSS expression, resolved like `glow`. */
  badge?: CardBadge;
  /** Write `badge` as a giant outline word behind the landed card (slice, burst). */
  badgeWord?: boolean;
  /** Bands of the stage, in css px, the host draws over — the scene keeps the pack and card clear of them. */
  reserveTop?: number;
  reserveBottom?: number;
  /** Where the winner's own card and plinth would be — the scene rests its card exactly there. */
  rest?: WinnerCardRest;
  /** The player's own mute: the library plays the ceremony's score on the operator's settings. */
  muted?: boolean;
  /** The pull's badge, once known — the library lands the card harder for a bigger pull. */
  soundBadge?: string | null;
  /** The scene has drawn the pack — and again whenever it re-lays it out. */
  onReady?: () => void;
  /** The pack is committed to opening — the cut past the point of no return,
   *  the charge full, the carousel's copy chosen. */
  onCommitted?: () => void;
  /** The lid is off / the pack has burst / the chosen pack has dissolved. */
  onOpened?: () => void;
  /** The whole ceremony has played out. */
  onRevealed?: () => void;
  /**
   * New settings could not be applied on the spot — the scene is mid-ceremony
   * or holding a revealed card — and wait for `reset()`. An editor resets here.
   */
  onOptionsDeferred?: () => void;
  /** The scene could not run: chunk failed, no WebGL, artwork refused. Fires once. */
  onError?: (message: string) => void;
  className?: string;
}

/**
 * Artwork as a blob URL for the scene. The library loads textures with a CORS
 * `fetch`, and a copy of the same URL that a plain `<img>` left in the HTTP
 * cache — dev and staging render the raw URL that way; production serves
 * `<img>` from the cdn-cgi proxy under another URL — carries no CORS headers
 * and fails that fetch even though the CDN allows the origin. So the artwork
 * is fetched here: from the cache when the copy there is usable, past it when
 * it is not, and handed over as a blob URL the scene loads without a request.
 */
async function fetchArtwork(url: string): Promise<string> {
  let response: Response;
  try {
    response = await fetch(url, { mode: 'cors' });
  } catch {
    response = await fetch(url, { mode: 'cors', cache: 'reload' });
  }
  if (!response.ok) throw new Error(`could not load ${url} (${response.status})`);
  return URL.createObjectURL(await response.blob());
}

/**
 * One artwork as a blob URL, revoked when the source changes or on unmount.
 * `null` while loading; `undefined` when there is no source; `onError` when
 * the fetch fails (the caller decides whether that is fatal).
 *
 * Re-fetches on a new `url` only. The blob URL is part of the scene's boot
 * key, so fetching again for any other reason — a parent re-rendering with a
 * fresh callback — would tear the scene down mid-ceremony.
 */
function useArtworkBlob(
  url: string | undefined,
  onError: (message: string) => void,
): string | null | undefined {
  const [blobUrl, setBlobUrl] = useState<string | null | undefined>(url ? null : undefined);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!url) {
      setBlobUrl(undefined);
      return undefined;
    }
    let alive = true;
    let created: string | null = null;
    setBlobUrl(null);
    fetchArtwork(url)
      .then((blob) => {
        created = blob;
        if (alive) setBlobUrl(blob);
      })
      .catch((error: unknown) => {
        if (alive) onErrorRef.current(error instanceof Error ? error.message : String(error));
      });
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  return blobUrl;
}

/**
 * The pack-opener-js scene as a reveal stage. Nothing is drawn until the
 * scene draws the pack itself: it fades the pack in over its own intro, and
 * anything shown in its place before that — a poster at another size — only
 * made the hand-over visible as a jump. So the stage stays empty for the
 * chunk (warmed at boot, see `preloadOpener`) and the artwork, then the scene
 * owns it. Falls back to nothing when the scene cannot run — the preview
 * shows the error, stage 2 switches the player to the slot lane.
 *
 * Callbacks are read through a ref: a parent re-rendering with new closures
 * (the reveal machine does, on every state change) must never restart the
 * scene or reload its artwork.
 */
export const PackOpenerStage = forwardRef<PackOpenerStageHandle, PackOpenerStageProps>(
  function PackOpenerStage(
    {
      animation,
      packUrl,
      cardUrl,
      pedestalUrl,
      glow,
      facts,
      badge,
      badgeWord,
      reserveTop,
      reserveBottom,
      rest,
      muted,
      soundBadge,
      onReady,
      onCommitted,
      onOpened,
      onRevealed,
      onOptionsDeferred,
      onError,
      className,
    },
    ref,
  ) {
    const [Opener, setOpener] = useState<OpenerComponent | null>(null);
    const [failed, setFailed] = useState(false);
    const openerRef = useRef<PackOpenerHandle>(null);
    const handlers = useRef({
      onReady,
      onCommitted,
      onOpened,
      onRevealed,
      onOptionsDeferred,
      onError,
    });
    handlers.current = {
      onReady,
      onCommitted,
      onOpened,
      onRevealed,
      onOptionsDeferred,
      onError,
    };
    // The library may emit several errors for one failure — report the first
    const reported = useRef(false);
    // The scene names the blob it was handed; the host wants to hear which
    // artwork that was
    const sources = useRef({ packUrl, packBlob: null as string | null });

    const fail = useCallback((message: string) => {
      setFailed(true);
      if (reported.current) return;
      reported.current = true;
      const { packUrl: source, packBlob } = sources.current;
      handlers.current.onError?.(packBlob ? message.split(packBlob).join(source) : message);
    }, []);

    // A new pack or mechanic gets a fresh chance: the failure was the last scene's
    useEffect(() => {
      setFailed(false);
      reported.current = false;
    }, [packUrl, animation.variant]);
    const ready = useCallback(() => handlers.current.onReady?.(), []);
    const committed = useCallback(() => handlers.current.onCommitted?.(), []);
    const opened = useCallback(() => handlers.current.onOpened?.(), []);
    const revealed = useCallback(() => handlers.current.onRevealed?.(), []);
    const optionsDeferred = useCallback(() => handlers.current.onOptionsDeferred?.(), []);

    const packBlob = useArtworkBlob(packUrl, fail);
    sources.current = { packUrl, packBlob: packBlob ?? null };
    // The card is optional: without it the scene finishes on a blank card.
    // So is the stand: without ours the scene draws the one it ships with
    const ignore = useCallback(() => {}, []);
    const cardBlob = useArtworkBlob(cardUrl, ignore);
    const pedestalBlob = useArtworkBlob(pedestalUrl, ignore);

    useEffect(() => {
      let alive = true;
      loadOpener()
        .then((component) => {
          if (alive) setOpener(() => component);
        })
        .catch((error: unknown) => {
          if (alive) fail(error instanceof Error ? error.message : String(error));
        });
      return () => {
        alive = false;
      };
    }, [fail]);

    const badgeLabel = badge?.label;
    const badgeColor = badge?.color;
    const options = useMemo(
      () =>
        packBlob
          ? toPackOpenerOptions(animation, {
              packUrl: packBlob,
              glow: resolveCssColor(glow),
              reserve: { top: reserveTop ?? 0, bottom: reserveBottom ?? 0 },
              ...(rest ? { rest } : {}),
              ...(cardBlob ? { cardUrl: cardBlob } : {}),
              ...(pedestalBlob ? { pedestalUrl: pedestalBlob } : {}),
              ...(facts ? { facts } : {}),
              ...(badgeLabel && badgeColor
                ? { badge: { label: badgeLabel, color: resolveCssColor(badgeColor) } }
                : {}),
              ...(badgeWord ? { badgeWord } : {}),
              // The score is the library's: the player's mute reaches a running
              // scene at once, the badge decides the landing when it is cued
              sound: { muted: muted ?? false, badge: soundBadge ?? null },
            })
          : null,
      [
        animation,
        packBlob,
        cardBlob,
        pedestalBlob,
        glow,
        facts,
        badgeLabel,
        badgeColor,
        badgeWord,
        reserveTop,
        reserveBottom,
        rest,
        muted,
        soundBadge,
      ],
    );

    useImperativeHandle(
      ref,
      () => ({
        play: () => openerRef.current?.autoSlice(),
        reset: () => openerRef.current?.reset(),
        shuffle: () => openerRef.current?.shuffle(),
        setEnabled: (enabled: boolean) => openerRef.current?.setEnabled(enabled),
      }),
      [],
    );

    return (
      <div className={`relative ${className ?? ''}`}>
        {!failed && Opener && options && (
          <Opener
            ref={openerRef}
            options={options}
            className="absolute inset-0"
            onReady={ready}
            onError={fail}
            onCommitted={committed}
            onOpenComplete={opened}
            onRevealComplete={revealed}
            onOptionsDeferred={optionsDeferred}
          />
        )}
      </div>
    );
  },
);
